import { Link } from "react-router-dom";
import Seo from "../../components/Seo";

const UPDATED = "December 5, 2025";

export default function TermsOfService() {
  return (
    <div className="bg-stone-50 min-h-screen" data-testid="terms-page">
      <Seo
        title="Terms of Service"
        description="Terms of Service for using the Gens Realty, LLC website and services."
        path="/terms"
      />
      <article className="max-w-4xl mx-auto px-6 lg:px-10 pt-14 pb-24">
        <div className="text-[10px] uppercase tracking-[0.3em] text-[#E63946] mb-3">Legal</div>
        <h1 className="font-display text-4xl lg:text-6xl font-light tracking-tighter">Terms of Service</h1>
        <p className="text-sm text-gray-500 mt-4">Last updated: {UPDATED}</p>

        <div className="mt-10 space-y-8 text-[#333] leading-relaxed">
          <Note>
            This document is a template starting point. Please have your attorney review and adapt
            before relying on it for live operations.
          </Note>

          <Section title="1. Acceptance of terms">
            <p>
              By accessing or using <Link to="/" className="text-[#E63946] underline">this website</Link>{" "}
              (the "Site") operated by Gens Realty, LLC ("Gens Realty," "we," "us"), you agree to be
              bound by these Terms of Service. If you do not agree, do not use the Site.
            </p>
          </Section>

          <Section title="2. No legal, tax, or financial advice">
            <p>
              Information on this Site is for general informational purposes only and is not legal, tax,
              accounting, financial, or investment advice. Consult appropriate professionals for advice
              specific to your situation.
            </p>
          </Section>

          <Section title="3. Listings and IDX data">
            <p>
              Property listings displayed via the HAR.com IDX system or otherwise are provided by third
              parties. We do not warrant the accuracy, completeness, or timeliness of any listing.
              Properties may be sold, withdrawn, or modified at any time without notice. Always verify
              listing details with a Gens Realty agent before relying on them.
            </p>
          </Section>

          <Section title="4. Mortgage information">
            <p>
              Mortgage products and any application content reference{" "}
              <strong>Edge Home Finance Corporation</strong> (NMLS #891464). Pierre Joseph (NMLS #1978260)
              is a licensed Loan Originator with Edge Home Finance and a separately-licensed Texas Realtor
              (#0537207) with Gens Realty, LLC. Gens Realty and Edge Home Finance are{" "}
              <strong>separate companies</strong>. You are not required to use either for the purchase,
              sale, or refinance of property.
            </p>
            <p>
              Information shown is illustrative only and is not a commitment to lend. Loan approval is
              subject to credit review, property appraisal, underwriting, and program guidelines. Rates,
              terms, and programs are subject to change without notice.
            </p>
          </Section>

          <Section title="5. AI assistant ('Ava')">
            <p>
              Our AI chat assistant provides general information only. Outputs may be inaccurate or
              outdated. Do not rely on the chatbot for legally or financially binding decisions. For a
              binding answer, contact a Gens Realty agent or, for mortgage matters, an Edge Home Finance
              Loan Originator.
            </p>
          </Section>

          <Section title="6. Acceptable use">
            <p>You agree not to:</p>
            <ul className="list-disc pl-6 space-y-2">
              <li>Use the Site for unlawful purposes.</li>
              <li>Scrape, harvest, or replicate listings or content without permission.</li>
              <li>Impersonate another person or upload misleading information.</li>
              <li>Disrupt or attempt to compromise the Site's security.</li>
            </ul>
          </Section>

          <Section title="7. Intellectual property">
            <p>
              The Site's content, design, and trademarks (including "Gens Realty") are owned by us or our
              licensors. You may not copy, modify, or reuse them without prior written permission.
            </p>
          </Section>

          <Section title="8. Disclaimers">
            <p>
              THE SITE IS PROVIDED "AS IS" AND "AS AVAILABLE." TO THE MAXIMUM EXTENT PERMITTED BY LAW, WE
              DISCLAIM ALL WARRANTIES, EXPRESS OR IMPLIED, INCLUDING WARRANTIES OF MERCHANTABILITY,
              FITNESS FOR A PARTICULAR PURPOSE, AND NON-INFRINGEMENT.
            </p>
          </Section>

          <Section title="9. Limitation of liability">
            <p>
              TO THE MAXIMUM EXTENT PERMITTED BY LAW, GENS REALTY AND ITS AFFILIATES, OFFICERS, AGENTS,
              AND EMPLOYEES SHALL NOT BE LIABLE FOR ANY INDIRECT, INCIDENTAL, SPECIAL, CONSEQUENTIAL, OR
              PUNITIVE DAMAGES ARISING FROM YOUR USE OF THE SITE.
            </p>
          </Section>

          <Section title="10. Governing law">
            <p>
              These Terms are governed by the laws of the State of Texas, without regard to conflict-of-law
              principles. Any dispute shall be resolved in the state or federal courts located in Harris
              County, Texas.
            </p>
          </Section>

          <Section title="11. Changes">
            <p>
              We may update these Terms from time to time. The "Last updated" date reflects the latest
              revision. Continued use of the Site constitutes acceptance.
            </p>
          </Section>

          <Section title="12. Contact">
            <p>
              Gens Realty, LLC · 14911 Heritage Wood Drive, Houston, TX 77082 ·{" "}
              <a href="mailto:PJoseph@gensrealty.com" className="text-[#E63946] underline">PJoseph@gensrealty.com</a>
            </p>
          </Section>
        </div>
      </article>
    </div>
  );
}

function Section({ title, children }) {
  return (
    <section>
      <h2 className="font-display text-2xl font-semibold text-[#1A1A1A] mb-3">{title}</h2>
      <div className="space-y-3">{children}</div>
    </section>
  );
}

function Note({ children }) {
  return (
    <div className="bg-amber-50 border border-amber-200 rounded-md px-5 py-4 text-sm text-amber-900">
      <strong>Note:</strong> {children}
    </div>
  );
}
